import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useQueryClient } from 'react-query';
import { stats } from '@fiverr-private/obs';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { METRICS_PREFIX } from '../../constants';
import { reportTimeMetric } from '../../monitoring';
import { classifyIntent, getIntentClassification, IntentClassification, IntentClassificationResponse } from '../../api';
import { logger } from '../../utils/logger';

export const INTENT_CLASSIFICATION_QUERY_TIMEOUT_MS = 10 * 1000;
export const INTENT_CLASSIFICATION_QUERY_RETRY_DELAY_MS = 500;

const requestType = 'intent_classification';

export const useIntentClassification = () => {
    const { setIntentClassification, userInput, contextId } = useBriefCreationContext();

    const handleError = (error?: Error) => {
        logger.warn(new Error('Failed during intent classification of user input'), { cause: error });
        stats.count(`${METRICS_PREFIX}.${requestType}`, `error`);
    };

    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const handleIntentClassification = async (
        intentClassificationResponse: AxiosResponse<IntentClassificationResponse>
    ) => {
        if (!intentClassificationResponse) {
            handleError();
            return;
        }

        const { data: intentClassification } = intentClassificationResponse;

        if (!intentClassification?.classification) {
            handleError(new Error('No classification found'));
            return;
        }

        const { classification } = intentClassification;

        setIntentClassification(classification);
        stats.count(`${METRICS_PREFIX}.${requestType}`, classification);
        logger.info('Intent classification result', { classification, userInput });
        return classification;
    };

    const intentClassification = async ({
        userInput,
        abortController,
    }: {
        userInput: string;
        abortController: AbortController;
    }): Promise<IntentClassification | undefined> => {
        setIsLoading(true);
        const intentClassificationStartTime = Date.now();

        try {
            const {
                data: { poll_id: intentClassificationPollingId },
            } = await queryClient.fetchQuery({
                queryKey: [requestType, userInput],
                queryFn: () => classifyIntent({ userInput, context_id: contextId, abortController }),
                cacheTime: 0,
                retry: false,
            });

            if (!intentClassificationPollingId) {
                handleError();
                return;
            }

            if (!userInput) {
                return;
            }

            const result = await queryClient.fetchQuery({
                queryKey: [requestType, intentClassificationPollingId],
                queryFn: () => getIntentClassification({ pollingId: intentClassificationPollingId, abortController }),
                retry: INTENT_CLASSIFICATION_QUERY_TIMEOUT_MS / INTENT_CLASSIFICATION_QUERY_RETRY_DELAY_MS,
                retryDelay: INTENT_CLASSIFICATION_QUERY_RETRY_DELAY_MS,
            });

            return await handleIntentClassification(result);
        } catch (error) {
            handleError(error as Error);
        } finally {
            setIsLoading(false);
            reportTimeMetric({
                metricName: requestType,
                time: Date.now() - intentClassificationStartTime,
                timeout: INTENT_CLASSIFICATION_QUERY_TIMEOUT_MS,
            });
        }
    };

    return {
        classifyIntent: intentClassification,
        isLoading,
    };
};
